import { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Navigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  AUTH_EMAIL_PARAM,
  AUTH_RETURN_TO_PARAM,
  AUTH_SOURCE_PARAM,
  AUTH_VIEW_PARAM,
  RESET_PASSWORD_VIEW,
  ResetPasswordView,
  SIGN_IN_VIEW,
} from 'common/constants/auth';
import { Alert, Button } from 'components/ui';
import { buildResendActivationUrl, RESEND_ACTIVATION_LINK_LABEL } from 'pages/Auth/ActivateAccount/ResendActivation';
import {
  AuthExtraActions,
  AuthFormHeader,
  AuthIdentifierRequestForm,
  AuthPageStack,
  AuthResendLabel,
  AuthSignInFooterPrompt,
  AuthStatusActionLink,
  AuthSuccessPanel,
  AuthTitleText,
} from 'pages/Auth/components';
import {
  useAuthInitialFocus,
  useAuthResendCooldown,
  useAuthSession,
  useAuthViewTransition,
  useClearFieldErrorOnValueChange,
  useSendPasswordReset,
} from 'pages/Auth/hooks';
import {
  buildAuthSearchParams,
  buildSignInUrl,
  clearFieldErrorsByType,
  isEmailLikeIdentifierReady,
  replaceSearchParamsIfChanged,
  sanitizeReturnTo,
} from 'pages/Auth/utils';
import {
  RESET_PASSWORD_EMAIL_LABEL,
  RESET_PASSWORD_EMAIL_PLACEHOLDER,
  RESET_PASSWORD_EMAIL_SENT_DESCRIPTION,
  RESET_PASSWORD_EMAIL_SENT_TITLE,
  RESET_PASSWORD_RESEND_ACTION_LABEL,
  RESET_PASSWORD_RESEND_ERROR,
  RESET_PASSWORD_RESEND_MISSING_EMAIL_ERROR,
  RESET_PASSWORD_SUBMIT_ERROR,
  RESET_PASSWORD_SUBMIT_LABEL,
  RESET_PASSWORD_SUBMIT_LATER_ERROR,
  RESET_PASSWORD_TITLE_LEAD,
  RESET_PASSWORD_TITLE_TAIL,
} from './ResetPassword.constants';
import { RESET_PASSWORD_SCHEMA } from './ResetPassword.schema';
import { RESET_PASSWORD_TEST_IDS } from './ResetPassword.testIds';
import type {
  ResetPasswordFormValues,
  ResetPasswordRequestActionLinksProps,
  ResetPasswordSuccessActionProps,
} from './ResetPassword.types';
import { didAuthActionSucceed, getAuthActionErrorStatus } from './ResetPassword.utils';

const getResetPasswordView = (value: string | null): ResetPasswordView =>
  value === RESET_PASSWORD_VIEW.EMAIL_SENT ? RESET_PASSWORD_VIEW.EMAIL_SENT : RESET_PASSWORD_VIEW.REQUEST;

const ResetPasswordRequestActionLinks = ({
  disabled = false,
  resendActivationHref,
  signInHref,
}: ResetPasswordRequestActionLinksProps) => (
  <>
    <AuthStatusActionLink<typeof RouterLink>
      component={RouterLink}
      disabled={disabled}
      to={resendActivationHref}
    >
      {RESEND_ACTIVATION_LINK_LABEL}
    </AuthStatusActionLink>
    <AuthSignInFooterPrompt disabled={disabled} to={signInHref} />
  </>
);

const ResetPasswordSuccessAction = ({
  disabled = false,
  signInHref,
}: ResetPasswordSuccessActionProps) => (
  <AuthSignInFooterPrompt disabled={disabled} replace to={signInHref} />
);

const ResetPassword = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sendPasswordReset = useSendPasswordReset({ useGlobalLoading: false });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isCoolingDown, remainingSeconds, startCooldown } = useAuthResendCooldown();
  const { hasSession } = useAuthSession();
  const view = getResetPasswordView(searchParams.get(AUTH_VIEW_PARAM));
  const email = searchParams.get(AUTH_EMAIL_PARAM) ?? '';
  const source = searchParams.get(AUTH_SOURCE_PARAM);
  const safeReturnTo = sanitizeReturnTo(searchParams.get(AUTH_RETURN_TO_PARAM));
  const { isViewTransitionPending, startViewTransition } = useAuthViewTransition(view);
  const isRequestBusy = isSubmitting || isViewTransitionPending;
  const resendActionLabel = (
    <AuthResendLabel label={RESET_PASSWORD_RESEND_ACTION_LABEL} remainingSeconds={isCoolingDown ? remainingSeconds : undefined} />
  );

  const form = useForm<ResetPasswordFormValues>({
    defaultValues: {
      email,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(RESET_PASSWORD_SCHEMA),
  });
  const watchedEmail = form.watch('email');
  const isSubmitDisabled = isRequestBusy || !isEmailLikeIdentifierReady(watchedEmail);
  const resendActivationHref = useMemo(
    () =>
      buildResendActivationUrl({
        email: view === RESET_PASSWORD_VIEW.REQUEST ? watchedEmail : email,
        returnTo: safeReturnTo,
        source,
      }),
    [email, safeReturnTo, source, view, watchedEmail]
  );
  const signInHref = useMemo(
    () =>
      buildSignInUrl({
        returnTo: safeReturnTo,
        source,
        view: SIGN_IN_VIEW.IDENTIFIER,
      }),
    [safeReturnTo, source]
  );

  const clearEmailServerError = useCallback(() => {
    clearFieldErrorsByType(form, ['email'], ['server']);
  }, [form]);

  const dismissSubmitError = useCallback(() => {
    setSubmitError(null);
    clearEmailServerError();
  }, [clearEmailServerError]);

  // Only the email-sent state persists the submitted address, so refresh can still drive resend without preserving request-step edits.
  useEffect(() => {
    const shouldStayOnEmailSent = view === RESET_PASSWORD_VIEW.EMAIL_SENT && Boolean(email);
    const nextParams = buildAuthSearchParams({
      email: shouldStayOnEmailSent ? email : null,
      returnTo: safeReturnTo,
      source,
      view: shouldStayOnEmailSent ? RESET_PASSWORD_VIEW.EMAIL_SENT : null,
    });

    replaceSearchParamsIfChanged(searchParams, nextParams, params => {
      setSearchParams(params, { replace: true });
    });
  }, [email, safeReturnTo, searchParams, setSearchParams, source, view]);
  useClearFieldErrorOnValueChange({
    errorTypes: ['server'],
    fieldNames: ['email'],
    form,
    onClear: dismissSubmitError,
    shouldClear: () => Boolean(submitError),
    values: [watchedEmail],
  });

  const focusResetEmailField = useCallback(() => {
    form.setFocus('email');
  }, [form]);

  useAuthInitialFocus(focusResetEmailField, view === RESET_PASSWORD_VIEW.REQUEST && !isRequestBusy);

  const hasLoggedInUser = useMemo(() => hasSession, [hasSession]);

  if (hasLoggedInUser) {
    return <Navigate replace to={buildSignInUrl({ view: SIGN_IN_VIEW.WORKSPACE_SELECTION })} />;
  }

  // Reset-password only owns its status step in the URL; the request form never persists partially edited input.
  const replaceViewState = (nextView: ResetPasswordView, nextEmail?: string) => {
    const nextParams = buildAuthSearchParams({
      email: nextView === RESET_PASSWORD_VIEW.EMAIL_SENT ? nextEmail ?? null : null,
      returnTo: safeReturnTo,
      source,
      view: nextView === RESET_PASSWORD_VIEW.EMAIL_SENT ? nextView : null,
    });

    startTransition(() => {
      setSearchParams(nextParams, { replace: true });
    });
  };

  const handleSubmit = form.handleSubmit(async values => {
    if (isSubmitting) {
      return;
    }

    clearEmailServerError();
    setSubmitError(null);
    setIsSubmitting(true);

    const response = await sendPasswordReset(values.email.trim());

    // Reset-password endpoints succeed with empty 204 responses, so success must be inferred from the lack of an HTTP error.
    if (didAuthActionSucceed(response)) {
      startCooldown();
      startViewTransition(RESET_PASSWORD_VIEW.EMAIL_SENT);
      replaceViewState(RESET_PASSWORD_VIEW.EMAIL_SENT, values.email.trim());
      setIsSubmitting(false);
      return;
    }

    const errorMessage = getAuthActionErrorStatus(response) !== null
      ? RESET_PASSWORD_SUBMIT_ERROR
      : RESET_PASSWORD_SUBMIT_LATER_ERROR;

    form.setError('email', { message: errorMessage, type: 'server' });
    setSubmitError(errorMessage);
    setIsSubmitting(false);
  });

  const resendResetEmail = async () => {
    if (isSubmitting) {
      return;
    }

    if (!email) {
      setSubmitError(RESET_PASSWORD_RESEND_MISSING_EMAIL_ERROR);
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);
    const response = await sendPasswordReset(email);

    if (!didAuthActionSucceed(response)) {
      setSubmitError(RESET_PASSWORD_RESEND_ERROR);
    } else {
      startCooldown();
    }

    setIsSubmitting(false);
  };

  return (
    <AuthPageStack>
      <AuthFormHeader title={<AuthTitleText lead={RESET_PASSWORD_TITLE_LEAD} tail={RESET_PASSWORD_TITLE_TAIL} />} />
      {submitError ? (
        <Alert onClose={dismissSubmitError} severity="error" visualVariant="inlineError">
          {submitError}
        </Alert>
      ) : null}
      {view === RESET_PASSWORD_VIEW.REQUEST ? (
        <AuthIdentifierRequestForm
          disabled={isRequestBusy}
          fieldName="email"
          form={form}
          hiddenErrorTypes={['server']}
          id="auth-reset-password-email"
          label={RESET_PASSWORD_EMAIL_LABEL}
          onSubmit={handleSubmit}
          placeholder={RESET_PASSWORD_EMAIL_PLACEHOLDER}
          submitDisabled={isSubmitDisabled}
          submitLabel={RESET_PASSWORD_SUBMIT_LABEL}
          submitLoading={isRequestBusy}
          submitTestId={RESET_PASSWORD_TEST_IDS.submitButton}
          submitVisualVariant="primary"
        />
      ) : (
        <AuthSuccessPanel
          description={RESET_PASSWORD_EMAIL_SENT_DESCRIPTION}
          extraAction={<ResetPasswordSuccessAction disabled={isSubmitting} signInHref={signInHref} />}
          mainAction={
            <Button
              disabled={isCoolingDown}
              loading={isSubmitting}
              loadingBehavior="inline"
              onClick={resendResetEmail}
              type="button"
              variant="outlined"
              visualVariant="primaryOutline"
            >
              {resendActionLabel}
            </Button>
          }
          title={RESET_PASSWORD_EMAIL_SENT_TITLE}
        />
      )}

      {view === RESET_PASSWORD_VIEW.REQUEST ? (
        <AuthExtraActions>
          <ResetPasswordRequestActionLinks
            disabled={isRequestBusy}
            resendActivationHref={resendActivationHref}
            signInHref={signInHref}
          />
        </AuthExtraActions>
      ) : null}
    </AuthPageStack>
  );
};

export default ResetPassword;
